/**
 * Share / copy the ACTUAL image bytes — never the image URL.
 *
 * Product rule: R2 file links (files.sohozkaj.com) must not be exposed to
 * users, so every share path either attaches the real file via the Web Share
 * API (mobile share sheet → WhatsApp/Gmail get the image itself) or copies the
 * image to the clipboard so the user pastes it into the chat / mail (desktop,
 * where wa.me/mailto links can only carry text).
 *
 * Callers supply a `getBlob` that resolves the image bytes through the backend
 * (e.g. imageGenerationService.downloadImage) — the R2 host sends no CORS
 * headers, so a direct browser fetch of the file URL would fail anyway.
 */

// The mobile share sheet lists WhatsApp/Gmail with the real file attached, so
// on phones we prefer navigator.share even for the explicit WhatsApp/Email
// menu items; on desktop those items copy the image + open the web app instead.
const isMobileUA = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Convert any image blob to PNG — the only image MIME type Chromium accepts in
// navigator.clipboard.write().
export async function toPngBlob(blob) {
    if (blob.type === 'image/png') return blob;
    const bitmap = await createImageBitmap(blob);
    try {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        return await new Promise((resolve, reject) => {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG conversion failed'))), 'image/png');
        });
    } finally {
        bitmap.close?.();
    }
}

/**
 * Copy an image to the clipboard. Accepts a Blob or Promise<Blob>.
 * Tries the promise-payload ClipboardItem form first (required on Safari,
 * where the user-gesture window expires while the blob downloads); older
 * Chromium rejects promise payloads, so it retries with the awaited blob.
 */
export async function copyImageToClipboard(blobOrPromise) {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        throw new Error('clipboard-unsupported');
    }
    // The Clipboard API refuses to write unless the document is focused. Async image
    // work beforehand (e.g. rasterising an invoice in a hidden iframe) can drop focus
    // → "Failed to execute 'write' on 'Clipboard': Document is not focused." Re-focus
    // the window immediately before each write to restore it.
    const png = await Promise.resolve(blobOrPromise).then(toPngBlob);
    try { window.focus(); } catch { /* ignore */ }
    try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    } catch (err) {
        // Focus can be lost between the await above and the write (iframe teardown);
        // wait a tick for the tab to regain focus, then retry once.
        if (/not focused/i.test(err?.message || '')) {
            await new Promise((r) => setTimeout(r, 150));
            try { window.focus(); } catch { /* ignore */ }
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
        } else {
            throw err;
        }
    }
}

/**
 * Share the image file to a target.
 *
 * @param {() => Promise<Blob>} getBlob  resolves the image bytes (via backend)
 * @param {'native'|'whatsapp'|'email'} target
 * @param {{ filename?: string, title?: string }} [opts]
 * @returns {Promise<'shared'|'copied'>}
 *   'shared' — the native share sheet handled it (file attached, nothing else to do)
 *   'copied' — the image is on the clipboard; WhatsApp Web / Gmail compose was
 *              opened for the explicit targets — caller should toast a paste hint.
 * Rejects with AbortError when the user closes the native share sheet.
 */
export async function shareImageToTarget(getBlob, target, { filename = 'sohozkaj-image.jpg', title = 'SohozKaj' } = {}) {
    const blobPromise = Promise.resolve(getBlob());

    const preferNative = target === 'native' || isMobileUA();
    if (preferNative && navigator.share && navigator.canShare) {
        const blob = await blobPromise;
        const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title, files: [file] });
            return 'shared';
        }
    }

    await copyImageToClipboard(blobPromise);
    if (target === 'whatsapp') {
        window.open('https://web.whatsapp.com/', '_blank', 'noopener');
    } else if (target === 'email') {
        window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(title)}`, '_blank', 'noopener');
    }
    return 'copied';
}
